import { Injectable } from "@angular/core";
import { NGXLogger, NGXLoggerMonitor } from "ngx-logger";
import { environment } from "environments/environment";
import { LoggerMonitorMinimal } from "app/shared/helpers/logger-monitor.minimal";
import { LoggerMonitorDevelopment } from "app/shared/helpers/logger-monitor.development";
import { StorageKey } from "app/shared/enums/storage-key.enum";

@Injectable({
  providedIn: "root",
})
export class LoggerConfigService {
  // Number (index of array) to log-level mapping.
  // Used to enable us to set one of these strings in the localStorage instead of a number.
  // Makes life easier on production builds, as the web-app developer tools are not available.
  public static logLevels: string[] = [
    "TRACE",
    "DEBUG",
    "INFO",
    "LOG",
    "WARN",
    "ERROR",
    "FATAL",
    "OFF",
  ];

  constructor(public logger: NGXLogger) {
    this.applyLocalStorageLogLevel();

    this.logger.debug(`Disabling NGXLogger console output.`);
    this.logger.updateConfig({
      ...this.logger.getConfigSnapshot(),
      // We use custom monitors, so we have to disable
      // the default logging to avoid rendering everything twice.
      disableConsoleLogging: true,
    });

    let customLoggerMonitor: NGXLoggerMonitor = new LoggerMonitorDevelopment();

    if (
      // Minimal logger on production, because JS map files are not available,
      // so filenames and line numbers of LoggerMonitorDevelopment will not provide useful information.
      environment.production ||
      // Minimal logger in E2E environment, because the E2E HTML report handles the log messages
      // and the log CSS styles are of no use here. Also providing an additional timestamp.
      (window as any).e2e
    ) {
      customLoggerMonitor = new LoggerMonitorMinimal();
    }

    this.logger.registerMonitor(customLoggerMonitor);
    this.logger.debug(`Custom monitor for NGXLogger registered.`);
  }

  public applyLocalStorageLogLevel(): void {
    let localStorageValue: string | null = localStorage.getItem(
      StorageKey.LogLevel,
    );

    if (!localStorageValue) {
      const activeLogLevel: string =
        LoggerConfigService.logLevels[this.logger.getConfigSnapshot().level];
      this.logger.debug(
        "No log-level set in localStorage. Using environment log-level:",
        activeLogLevel,
      );

      return;
    }

    let parsedValue: number = parseInt(localStorageValue, 10);

    if (isNaN(parsedValue)) {
      localStorageValue = localStorageValue.toUpperCase();
      parsedValue = LoggerConfigService.logLevels.indexOf(localStorageValue);
    }

    if (parsedValue === -1) {
      this.logger.warn(
        `Failed to apply log-level set in localStorage. ` +
          `The localStorage value for '${StorageKey.LogLevel}' is invalid:`,
        localStorageValue,
      );

      return;
    }

    this.logger.log(
      `Applying log-level from localStorage:`,
      LoggerConfigService.logLevels[localStorageValue],
    );
    this.setLogLevel(parsedValue);
  }

  // Update the log-level and make sure the localStorage reflects the changes.
  public setLogLevel(logLevel: number): void {
    if (logLevel < 0 || logLevel > LoggerConfigService.logLevels.length) {
      this.logger.error(`Tried to set invalid log-level:`, logLevel);

      return;
    }

    localStorage.setItem(StorageKey.LogLevel, logLevel.toString());
    this.logger.updateConfig({
      ...this.logger.getConfigSnapshot(),
      level: logLevel,
    });

    this.logger.debug(`Log-level set to:`, logLevel);
  }
}
